<template>
  <BaseLoader v-if="!$store.getters.isLoaded(['orders', 'shelves'])" />
  <div v-else>
    <div class="d-flex justify-content-between align-items-center m-4">
      <h1 class="h4 m-0">Orders</h1>
      <button class="btn btn-sm btn-outline-secondary" @click="showReOpenLayer = true">Re-open Order</button>
    </div>
    <div v-for="prioKey in [1, 2, 3, 0]" :key="prioKey">
      <div v-if="ordersByPrioAndDay[prioKey]">
        <h2 class="h4 ml-4 mt-5">Prio {{ prioKey }}</h2>
        <div v-for="day in ordersByPrioAndDay[prioKey]" :key="day.dayString">
          <h3 class="h6 ml-4 mt-4">{{ day.dayLabel }}</h3>
          <div class="list-group">
            <router-link
              v-for="order in day.orders"
              :key="order.id"
              :to="`/orders/${order.id}`"
              class="list-group-item list-group-item-action order-overview__item"
              :class="{ 'list-group-item-success': order.state === 'shipped' }"
            >
              <div class="d-flex justify-content-between align-items-center">
                <h3 class="h5 text-truncate mr-2 mb-0">
                  <small v-if="order.residents && order.residents.length > 0">{{ order.residents.length }} 👵🏼</small>
                  <small v-if="order.kioskOrder" class="ml-1">🏪</small>
                  <small v-if="order.homeOrder" class="ml-1">🏠</small>
                  {{ order.organizationName }}
                </h3>
                <div class="d-flex align-items-center flex-shrink-0">
                  <span class="badge badge-warning mr-1" v-if="order.progress.missingCounts.products">
                    {{ order.progress.missingCounts.products }}
                    {{ order.progress.missingCounts.products === 1 ? 'Product' : 'Products' }} ({{
                      order.progress.missingCounts.items
                    }}
                    {{ order.progress.missingCounts.items === 1 ? 'Item' : 'Items' }})
                  </span>
                  <small class="ml-2 flex-shrink-0">{{ order.createdAt.toDate() | date }}</small>
                </div>
              </div>
              <div class="d-flex align-items-center">
                <div class="order-overview__progress" style="width: 30%">
                  <span
                    class="order-overview__progress__label"
                    :class="order.progress.picked === 100 && !order.state.shipped ? 'text-success' : ''"
                  >
                    {{ order.progress.picked }} % picked
                  </span>
                  <div class="progress order-overview__progress__bar">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="`width: ${order.progress.picked}%`"
                      :aria-valuenow="order.progress.picked"
                      :class="order.progress.picked === 100 ? 'bg-success' : ''"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
                <div class="order-overview__progress" style="width: 70%">
                  <span
                    class="order-overview__progress__label"
                    :class="order.progress.packed === 100 && !order.status === 'shipped' ? 'text-success' : ''"
                  >
                    {{ order.progress.packed }} % packed
                  </span>
                  <div class="progress order-overview__progress__bar">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="`width: ${order.progress.packed}%`"
                      :aria-valuenow="order.progress.packed"
                      :class="order.progress.packed === 100 ? 'bg-success' : ''"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <div v-if="Object.keys(ordersByPrioAndDay).length === 0" class="alert alert-info" role="alert">No Open Orders</div>
    <ReOpenOrderLayer v-if="showReOpenLayer" @close="showReOpenLayer = false" />
  </div>
</template>

<script>
import { TotalItems, PickedItems, PackedItems, MissingItems, MissingItemCounts } from '@/helpers/Calculator'
import ReOpenOrderLayer from '@/components/ReOpenOrderLayer'
import moment from 'moment'

export default {
  components: { ReOpenOrderLayer },
  data() {
    return {
      showReOpenLayer: false
    }
  },
  methods: {
    getOrdersByDay(orders) {
      orders = orders
        .filter(o => !o.orderJobId)
        .map(order => {
          order.dayString = moment(order.createdAt.toDate()).format('YYYY-MM-DD')
          return order
        })
      let ordersByDay = _.groupBy(orders, 'dayString')
      ordersByDay = Object.keys(ordersByDay).map(dayString => {
        return {
          dayString,
          dayLabel: moment(dayString).calendar(null, {
            lastDay: '[Yesterday], MMM Do',
            sameDay: '[Today]',
            nextDay: '[Tomorrow]',
            lastWeek: 'dddd, MMM Do',
            nextWeek: 'dddd',
            sameElse: 'L'
          }),
          orders: ordersByDay[dayString]
        }
      })
      return ordersByDay
    }
  },
  computed: {
    orders() {
      if (!this.$store.getters.isLoaded(['orders', 'shelves'])) return null
      console.time('Overview/orders')
      const { orders, shelves } = this.$store.state

      orders.forEach((order, i) => {
        const totalItemsSum = sumItemCount(TotalItems([order]))
        const pickedItemsSum = sumItemCount(PickedItems([order]))
        const packedItemsSum = sumItemCount(PackedItems([order]))
        const missingItems = MissingItems([order], shelves)
        const missingCounts = MissingItemCounts(missingItems)
        orders[i].progress = {
          picked: Math.floor((pickedItemsSum / totalItemsSum) * 100),
          packed: Math.floor((packedItemsSum / totalItemsSum) * 100),
          missingCounts
        }
      })
      console.timeEnd('Overview/orders')
      return _.orderBy(orders, 'createdAt', 'asc')
    },
    ordersByPrioAndDay() {
      const prios = {
        1: [],
        2: [],
        3: [],
        0: []
      }
      this.orders.forEach(order => {
        const whPrio = typeof order.whPrio !== 'undefined' ? order.whPrio : 2
        prios[whPrio].push(order)
      })
      Object.keys(prios).forEach(prioKey => {
        if (prios[prioKey].length === 0) {
          delete prios[prioKey]
        } else {
          prios[prioKey] = this.getOrdersByDay(prios[prioKey])
        }
      })
      return prios
    }
  },
  filters: {
    date(date) {
      return moment(date).format('MMM Do - HH:mm')
    }
  }
}

const sumItemCount = items => {
  let sum = 0
  Object.keys(items).forEach(key => {
    sum += items[key]
  })
  return sum
}
</script>

<style lang="scss" scoped>
.order-overview {
  &__progress {
    padding-right: 0.25rem;
    &__bar {
      height: 5px;
    }

    &__label {
      font-size: 0.75rem;
    }
  }
}
</style>
